/**
 * deepspace undeploy
 *
 * Removes a deployed app from *.app.space via the deploy worker.
 */

import { defineCommand } from 'citty'
import * as p from '@clack/prompts'
import { createSpinner } from '../lib/spinner'
import { ensureToken } from '../auth'
import { PLATFORM_URLS } from '../env'
import { resolveAppNameForEnv, readWranglerConfig, hasWranglerConfig } from '../lib/wrangler-env'

const DEPLOY_URL = process.env.DEEPSPACE_DEPLOY_URL ?? PLATFORM_URLS.deploy

export default defineCommand({
  meta: {
    name: 'undeploy',
    description: 'Remove a deployed DeepSpace app',
  },
  args: {
    name: {
      type: 'positional',
      description: 'App name to undeploy (reads from wrangler.toml if omitted)',
      required: false,
    },
    env: {
      type: 'string',
      description:
        'wrangler.toml [env.<name>] block whose deployed app to remove ' +
        '(e.g. --env staging). Ignored if a positional name is given.',
      required: false,
    },
  },
  async run({ args }) {
    let appName = args.name as string | undefined
    const envName = typeof args.env === 'string' && args.env.trim() ? args.env.trim() : undefined

    // If no name given, try to read from wrangler.toml in cwd. With --env
    // we look up `[env.<name>].name`; without it, the top-level `name`.
    if (!appName) {
      if (hasWranglerConfig(process.cwd())) {
        const config = readWranglerConfig(process.cwd())
        const nameRes = resolveAppNameForEnv(config, envName)
        if (!nameRes.ok) {
          p.cancel(nameRes.reason)
          process.exit(1)
        }
        appName = nameRes.name
      }
    }

    if (!appName) {
      p.cancel('Provide an app name or run from a DeepSpace app directory.')
      process.exit(1)
    }

    let token: string
    try {
      token = await ensureToken()
    } catch (err: unknown) {
      p.cancel(err instanceof Error ? err.message : String(err))
      process.exit(1)
    }

    p.intro(`Undeploying ${appName}`)
    const s = createSpinner()
    s.start('Removing...')

    const res = await fetch(`${DEPLOY_URL}/api/deploy/${appName}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })

    const body = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string }

    if (!res.ok || !body.success) {
      s.stop('Failed')
      p.cancel(body.error ?? `Undeploy error (${res.status})`)
      process.exit(1)
    }

    s.stop('Removed')
    p.outro(`${appName}.app.space has been taken down`)
  },
})
